import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { AoRepository } from '@/store/ao-state/ao-state.repository';
import { AoService } from '../ao.service';
import { AoWorkflowBlocksService } from '../ao-workflow-blocks.service';
import { BehaviorSubject, catchError, finalize, forkJoin, switchMap } from 'rxjs';
import { getInspectAoFormObject } from './utils/inspect-ao.form-object';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { PpmMilestone, WithMetadata } from '@/types';
import { PpmService } from '@/pages/ppm/ppm.service';
import { CurrencyService } from '@/pages/settings/currency/currency.service';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { FundingSourceService } from '@/pages/settings/funding-sources/funding-sources.service';

@Component({
  selector: 'sm-inspect-tender',
  imports: [CommonModule, PageHeaderComponent, FormBuilderComponent, StatusComponent, BlockBuilderComponent, SpinnerComponent],
  templateUrl: './inspect-tender.component.html',
  styleUrl: './inspect-tender.component.scss',
})
export class InspectTenderComponent implements OnInit, OnDestroy {
  aoRepository = inject(AoRepository);
  aoService = inject(AoService);
  ppmService = inject(PpmService);
  currencyService = inject(CurrencyService);
  fundingSourceService = inject(FundingSourceService);
  aoWorkflowBlocksService = inject(AoWorkflowBlocksService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  route = inject(ActivatedRoute);

  id = this.route.snapshot.params['id'];
  status = new BehaviorSubject<string>('');

  breadcrumb = [
    {
      label: 'ao.plural',
      routerLink: ['/call-for-tender'],
    },
    {
      label: 'ao.pages.inspectTender',
    },
  ];

  currencyOptions?: SelectOption[];
  financialSourcesOptions?: (SelectOption & { examenPrealable: boolean })[];
  milestones?: WithMetadata<PpmMilestone>;
  hasSlots = new BehaviorSubject<boolean>(false);
  hasFundingSources = new BehaviorSubject<boolean>(false);

  formObject = getFormBuilderObjectFactory();

  controlBlocks: DynamicBlocks[] = [];
  loading$ = new BehaviorSubject<boolean>(true);

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.aoRepository.reset();
  }

  loadData() {
    this.loading$.next(true);
    this.controlBlocks = this.aoWorkflowBlocksService.getInspectAoBlocks();

    forkJoin({
      ao: this.aoService.getAo(this.id),
      currencies: this.currencyService.getAll(),
      fundings: this.fundingSourceService.getAll(),
    })
      .pipe(
        switchMap(({ ao, currencies, fundings }) => {
          // you can use currencies if needed
          return this.ppmService.getPpm(ao.ppm.id).pipe(
            switchMap(ppm => {
              // process AO and PPM data
              this.aoRepository.set('response', ao);
              this.aoRepository.set('updateDto', {
                nom: ao.nom,
                description: ao.description,
                lotSepares: ao.lotSepares,
              });
              this.status.next(`ao.status.${ao.statusAppelOffre}`);

              const milestone = ppm?.realisations?.find(m => m.id === ao.realisation.id);
              this.hasSlots.next(!!milestone?.lots?.length);
              this.hasFundingSources.next(!!milestone?.financements?.length);

              this.currencyOptions = currencies.map(curr => ({ name: curr.description, code: curr.id }));
              this.financialSourcesOptions = fundings.map(ref => ({ name: ref.description, code: ref.id, examenPrealable: ref.bailleurDeFonds }));

              this.formObject = getInspectAoFormObject({
                store: this.aoRepository,
                hasSlots: this.hasSlots,
                hasFundingSources: this.hasFundingSources,
                currencyOptions: this.currencyOptions,
                financialSourcesOptions: this.financialSourcesOptions,
              });

              return []; // return empty observable to complete
            })
          );
        }),
        catchError(error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.message,
          });
          return [];
        }),
        finalize(() => this.loading$.next(false))
      )
      .subscribe();
  }
}
